import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { getAccountDeletionPreview, runAccountDeletion } from '../../../shared/utils/accountDeletion';

// Member-facing account deletion (Stage 40). Loads the situation preview, then either
// BLOCKS (user owns groups with records) or shows situation-aware warnings before the
// irreversible action. On success the auth row is gone -> clear + sign out + home.
// All member-facing copy is FRANNIE DRAFT (calm + clear, not alarming).
const DeleteAccountModal = ({ onClose }) => {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);   // null = loading
  const [ack, setAck] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let on = true;
    getAccountDeletionPreview()
      .then(p => { if (on) setPreview(p); })
      .catch(e => { if (on) setError(e.message || 'Could not load your account details.'); });
    return () => { on = false; };
  }, []);

  const blockedGroups = preview?.blocked_by_owned_groups || [];
  const isBlocked = blockedGroups.length > 0;

  const handleDelete = async () => {
    setWorking(true); setError(null);
    try {
      await runAccountDeletion();
      localStorage.clear();
      await supabaseClient.auth.signOut();
      navigate('/');
    } catch (e) {
      setError(e.message || 'Could not delete your account.');
      setWorking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-neutral-100 rounded-lg w-full max-w-md shadow-xl flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center px-6 py-4 border-b border-neutral-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-error-700">Delete my account</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">✕</button>
        </div>

        <div className="px-6 py-5 space-y-4 flex-1 overflow-y-auto">
          {preview === null && !error ? (
            <p className="text-sm text-neutral-400">Loading your account details…</p>
          ) : isBlocked ? (
            <div className="bg-warning-50 border border-warning-200 rounded-lg p-3">
              {/* FRANNIE DRAFT */}
              <p className="text-sm font-semibold text-warning-800 mb-1">Wind down your groups first</p>
              <p className="text-xs text-warning-700 mb-2">
                You organize groups that have records other members may rely on. Please delete or hand
                these over before deleting your account:
              </p>
              <ul className="text-xs text-warning-800 font-medium list-disc list-inside">
                {blockedGroups.map(g => <li key={g.id}>{g.name}</li>)}
              </ul>
            </div>
          ) : (
            <>
              {/* FRANNIE DRAFT — always shown */}
              <p className="text-sm text-neutral-700">
                This permanently deletes your account and removes your personal details. Your past
                contributions stay on record for your groups, shown as "Former member". This can't be undone.
              </p>

              {/* Export-first — always, emphasized if they hold a certified record */}
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
                {/* FRANNIE DRAFT */}
                <p className="text-xs font-semibold text-primary-800 mb-1">Save your record first</p>
                <p className="text-xs text-primary-700">
                  {preview.has_certified_record
                    ? 'You have a certified savings record that may be needed for a mortgage file. Download your certificate (a self-verifying PDF) before you delete — your account here is removed, but the PDF you save is yours to keep.'
                    : 'If you may need proof of your savings later (for example, a mortgage file), download any records you want to keep before deleting.'}
                </p>
              </div>

              {/* Mid-cycle group-impact */}
              {preview.outstanding_obligation && (
                <div className="bg-warning-50 border border-warning-200 rounded-lg p-3">
                  {/* FRANNIE DRAFT */}
                  <p className="text-xs text-warning-800">
                    You're in the middle of an active cycle with contributions still due. Leaving now may
                    affect your group — please let your organizer know.
                  </p>
                </div>
              )}

              {/* Collector rotation-disruption */}
              {preview.is_collector && (
                <div className="bg-warning-50 border border-warning-200 rounded-lg p-3">
                  {/* FRANNIE DRAFT */}
                  <p className="text-xs text-warning-800">
                    You're set to receive a payout as a collector in the rotation. Deleting your account
                    may disrupt the group's schedule — please coordinate with your organizer first.
                  </p>
                </div>
              )}

              <label className="flex items-start gap-2 text-xs text-neutral-700">
                <input type="checkbox" checked={ack} onChange={e => setAck(e.target.checked)} className="mt-0.5" />
                {/* FRANNIE DRAFT */}
                <span>I understand this permanently deletes my account and cannot be undone.</span>
              </label>
            </>
          )}

          {error && <p className="text-sm text-error-600">{error}</p>}
        </div>

        <div className="flex space-x-3 px-6 py-4 border-t border-neutral-200 flex-shrink-0">
          <button onClick={onClose} disabled={working} className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg text-neutral-700 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50">
            {isBlocked ? 'Close' : 'Cancel'}
          </button>
          {!isBlocked && preview !== null && (
            <button
              onClick={handleDelete}
              disabled={!ack || working}
              className="flex-1 px-4 py-2 bg-error-600 text-neutral-50 rounded-lg text-sm font-semibold hover:bg-error-700 disabled:opacity-50"
            >
              {working ? 'Deleting…' : 'Delete my account'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountModal;
